import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users, Award, Eye, EyeOff, Mail, Key } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEMES = [
  'Artificial Intelligence',
  'Blockchain',
  'AgriTech',
  'HealthTech',
  'FinTech',
  'EdTech',
  'Open Innovation'
];

const AdminJuryManagementSection = ({ token }) => {
  const [juryMembers, setJuryMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    expertise: [],
    max_assignments: 20
  });

  useEffect(() => {
    fetchJuryMembers();
  }, []);

  const fetchJuryMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/jury`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJuryMembers(response.data.jury || []);
    } catch (err) {
      toast.error('Failed to fetch jury members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/jury/create-v2`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Jury member created successfully');
      fetchJuryMembers();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create jury member');
    }
  };

  const handleDelete = async (juryId) => {
    if (!window.confirm('Are you sure you want to deactivate this jury member?')) return;
    try {
      await axios.delete(`${API}/admin/jury/${juryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Jury member deactivated');
      fetchJuryMembers();
    } catch (err) {
      toast.error('Failed to deactivate jury member');
    }
  };

  const toggleExpertise = (theme) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(theme)
        ? prev.expertise.filter(t => t !== theme)
        : [...prev.expertise, theme]
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      expertise: [],
      max_assignments: 20
    });
    setShowForm(false);
    setShowPassword(false);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="jury-management-section">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Jury Management</h2>
          <p className="text-sm text-gray-500">Create and manage jury accounts</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          data-testid="add-jury-btn"
        >
          <Plus size={18} />
          Add Jury Member
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{juryMembers.length}</div>
          <div className="text-sm text-gray-600">Total Jury</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {juryMembers.filter(j => j.is_active).length}
          </div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">
            {juryMembers.reduce((acc, j) => acc + (j.scores_given || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Scores Given</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {juryMembers.reduce((acc, j) => acc + (j.assigned_submissions?.length || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Assignments</div>
        </div>
      </div>

      {/* Create Jury Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold mb-4">Create Jury Account</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                  data-testid="jury-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email (Username) *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border rounded-lg pl-10 pr-3 py-2"
                    required
                    data-testid="jury-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Password *</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full border rounded-lg pl-10 pr-10 py-2"
                      required
                      data-testid="jury-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Expertise Areas</label>
                <div className="flex flex-wrap gap-2">
                  {THEMES.map(theme => (
                    <button
                      key={theme}
                      type="button"
                      onClick={() => toggleExpertise(theme)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.expertise.includes(theme)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Max Assignments</label>
                <input
                  type="number"
                  value={formData.max_assignments}
                  onChange={(e) => setFormData({ ...formData, max_assignments: parseInt(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2"
                  min="1"
                  max="100"
                  data-testid="jury-max-assignments-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Create Jury Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Jury Members List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Expertise</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Scores</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {juryMembers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No jury members yet. Click &quot;Add Jury Member&quot; to create one.
                  </td>
                </tr>
              ) : (
                juryMembers.map(jury => (
                  <tr key={jury.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Award className="text-purple-600" size={16} />
                        </div>
                        <span className="font-medium">{jury.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{jury.email || jury.username}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(jury.expertise || []).slice(0, 2).map(e => (
                          <span key={e} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                            {e}
                          </span>
                        ))}
                        {(jury.expertise || []).length > 2 && (
                          <span className="text-gray-500 text-xs">+{jury.expertise.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className="font-medium">{jury.scores_given || 0}</span>
                        <span className="text-gray-400 text-sm">/ {jury.max_assignments || 20}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        jury.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {jury.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(jury.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Deactivate"
                        data-testid={`delete-jury-${jury.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminJuryManagementSection;
